import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { MODEL_TEMPLATES, type ModelTemplate } from '../data/model-templates';
import { cn } from '@/lib/utils';

const MODEL_TYPE_LABELS: Record<string, string> = {
  STANDARD: 'Estandar',
  SUBSCRIPTION: 'Suscripcion',
  BUNDLE: 'Bundle',
  PROMOTIONAL: 'Promocional',
  COST_PLUS: 'Costo + Margen',
  LEASING: 'Leasing',
  MANAGED_SERVICE: 'Servicio',
  TIERED_VOLUME: 'Volumen',
  PROJECT_BASED: 'Proyecto',
};

interface TemplatePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (template: ModelTemplate) => void;
}

export function TemplatePickerDialog({ open, onOpenChange, onSelect }: TemplatePickerDialogProps) {
  const handleSelect = (template: ModelTemplate) => {
    onOpenChange(false);
    onSelect(template);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Seleccionar Template</DialogTitle>
          <DialogDescription>
            Elige un template con variables pre-configuradas o crea un modelo en blanco
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2">
          {MODEL_TEMPLATES.map((template) => {
            const Icon = template.icon;
            const isCustom = template.id === 'custom';

            return (
              <button
                key={template.id}
                type="button"
                onClick={() => handleSelect(template)}
                className={cn(
                  'flex flex-col items-start gap-3 rounded-lg border p-4 text-left transition-all hover:bg-accent hover:shadow-md cursor-pointer',
                  isCustom && 'border-dashed border-2'
                )}
              >
                <div className="flex items-center gap-3 w-full">
                  <div
                    className={cn(
                      'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg',
                      isCustom ? 'bg-muted' : 'bg-primary/10'
                    )}
                  >
                    <Icon
                      className={cn('h-5 w-5', isCustom ? 'text-muted-foreground' : 'text-primary')}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-sm truncate">{template.name}</h3>
                    {!isCustom && (
                      <Badge variant="outline" className="text-[10px] mt-0.5">
                        {MODEL_TYPE_LABELS[template.type] || template.type}
                      </Badge>
                    )}
                  </div>
                </div>

                <p className="text-xs text-muted-foreground line-clamp-2">{template.description}</p>

                <div className="flex items-center gap-2 w-full mt-auto">
                  {isCustom ? (
                    <Badge variant="secondary" className="text-[10px]">
                      Sin variables
                    </Badge>
                  ) : (
                    <>
                      <Badge variant="secondary" className="text-[10px]">
                        ~{template.variables.length} variables
                      </Badge>
                      {template.lookupTables && template.lookupTables.length > 0 && (
                        <Badge variant="secondary" className="text-[10px]">
                          {template.lookupTables.length} lookup
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] text-ok">
                        {template.targetMarginPercentage}% obj
                      </Badge>
                    </>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TemplatePickerDialog;
